/** Max timeout: 365 days. Min: 1 second. */
export const MIN_TIMEOUT_MS = 1000;
export const MAX_TIMEOUT_MS = 525600 * 60_000;

const DURATION_RE = /^(\d+(?:\.\d+)?)\s*([smhd])$/i;

/** Parse duration strings like 30s, 5m, 2h, 5d into milliseconds. Plain numbers = minutes. */
export function parseTimeoutDuration(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const match = trimmed.match(DURATION_RE);
  if (match) {
    const amount = Number(match[1]);
    if (!Number.isFinite(amount) || amount <= 0) return null;
    const unit = match[2].toLowerCase();
    const multipliers: Record<string, number> = {
      s: 1000,
      m: 60_000,
      h: 3_600_000,
      d: 86_400_000,
    };
    return clampTimeoutMs(amount * multipliers[unit]!);
  }

  const asMinutes = Number(trimmed);
  if (Number.isFinite(asMinutes) && asMinutes > 0) {
    return clampTimeoutMs(asMinutes * 60_000);
  }
  return null;
}

export function clampTimeoutMs(durationMs: number): number {
  return Math.max(MIN_TIMEOUT_MS, Math.min(Math.floor(durationMs), MAX_TIMEOUT_MS));
}

/** Human-readable label for confirm messages (e.g. "30 seconds", "5 days"). */
export function formatTimeoutDurationLabel(input: string): string {
  const ms = parseTimeoutDuration(input);
  if (ms == null) return input.trim() || "—";
  return formatTimeoutMsLabel(ms);
}

export function formatTimeoutMsLabel(ms: number): string {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return sec === 1 ? "1 second" : `${sec} seconds`;
  const min = Math.round(ms / 60_000);
  if (min < 60) return min === 1 ? "1 minute" : `${min} minutes`;
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return hours === 1 ? "1 hour" : `${hours} hours`;
  const days = Math.round(ms / 86_400_000);
  return days === 1 ? "1 day" : `${days} days`;
}

/** Compact duration for API when adjusting by a computed delta (ms). */
export function msToDurationToken(ms: number): string {
  const clamped = clampTimeoutMs(ms);
  if (clamped % 86_400_000 === 0) return `${clamped / 86_400_000}d`;
  if (clamped % 3_600_000 === 0) return `${clamped / 3_600_000}h`;
  if (clamped % 60_000 === 0) return `${clamped / 60_000}m`;
  return `${Math.round(clamped / 1000)}s`;
}
